import type { AdminUser } from "@/types/auth";

function normalizeRole(role: string) {
  return role.trim().toUpperCase();
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function decodeBase64Url(input: string): string | null {
  try {
    const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    if (typeof atob === "function") return atob(padded);
    return null;
  } catch {
    return null;
  }
}

function decodeAccessTokenClaims(accessToken: string): Record<string, unknown> | null {
  const [, payload] = accessToken.split(".");
  if (!payload) return null;
  const decoded = decodeBase64Url(payload);
  if (!decoded) return null;
  try {
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function extractRoles(claims: Record<string, unknown>): string[] {
  const btlRoles = asStringArray(claims.btl_roles);
  if (btlRoles.length > 0) return [...new Set(btlRoles)];
  return [...new Set(asStringArray(claims.roles))];
}

function hasDashboardAdminAccess(roles: string[]) {
  return roles.some((role) => {
    const normalized = normalizeRole(role);
    return normalized === "ADMIN" || normalized === "USER_ROLE_ADMIN";
  });
}

/**
 * Resolve the admin user from the cookie-login access token (JWT).
 *
 * The token already carries everything the dashboard needs — sub, email,
 * display_name/username, avatar_url, and btl_roles — so we decode it directly
 * instead of making a separate cross-origin AuthService/GetSession call to
 * admin-api (which AuthService doesn't serve, causing a CORS failure). This
 * matches the platform's cookie-based auth: cookie login -> JWT -> user.
 */
export async function resolveSessionUser(accessToken: string): Promise<AdminUser | null> {
  const claims = decodeAccessTokenClaims(accessToken);
  if (!claims) return null;

  const id = asString(claims.sub);
  const email = asString(claims.email);
  if (!id || !email) return null;

  const roles = extractRoles(claims);
  if (!hasDashboardAdminAccess(roles)) return null;

  const name =
    asString(claims.display_name).trim() || asString(claims.username).trim() || email || "Admin";
  const avatarUrl = asString(claims.avatar_url) || undefined;

  return { id, name, email, role: "admin", roles, avatarUrl };
}
